/**
 * Error Prevention System — Real-time validation warnings
 */
import { useMemo } from 'react';
import { AlertTriangle, CheckCircle, AlertCircle, Info } from 'lucide-react';

const SEVERITY = { error: 'bg-red-50 border-red-200 text-red-800', warn: 'bg-amber-50 border-amber-200 text-amber-800', info: 'bg-blue-50 border-blue-200 text-blue-800' };

export default function ErrorPrevention({ config = {}, calculations = {}, rates = {} }) {
  const alerts = useMemo(() => {
    const a = [];
    const { width = 4, height = 8, depth = 2, shelfCount = 3, drawerCount = 2 } = config;

    // Dimension validation
    if (width <= 0 || height <= 0 || depth <= 0) a.push({ severity: 'error', msg: 'Invalid dimensions: width, height, and depth must be > 0' });
    if (width > 12) a.push({ severity: 'warn', msg: `Width ${width}ft exceeds standard max (12ft). Verify structural support.` });
    if (height > 10) a.push({ severity: 'warn', msg: `Height ${height}ft exceeds standard ceiling clearance. Check site measurement.` });
    if (depth < 1.5) a.push({ severity: 'warn', msg: `Depth ${depth}ft is shallow. Standard hangers need min 1.75ft depth.` });
    if (depth > 3) a.push({ severity: 'warn', msg: `Depth ${depth}ft is unusually deep. Check site constraint.` });

    // Shelf span check (unsupported span > 3ft)
    if (width > 3 && shelfCount > 0) a.push({ severity: 'info', msg: `Shelf span ${width}ft > 3ft. Consider adding a vertical partition for structural support.` });

    // Drawer clash (too many drawers for height)
    const drawerZoneHeight = height - 1.6 - 3.25; // loft + hanging
    const drawerInches = (config.drawerHeight || 8) * drawerCount;
    const availableInches = drawerZoneHeight * 12;
    if (drawerInches > availableInches * 0.8) a.push({ severity: 'warn', msg: `Drawers take ${drawerInches}" of ${availableInches.toFixed(0)}" available zone (${((drawerInches/availableInches)*100).toFixed(0)}%). Reduce drawer count or height.` });

    // Material wastage
    (calculations.materialSummary || []).forEach(m => {
      if (m.wastage > 30) a.push({ severity: 'warn', msg: `High wastage on ${m.name}: ${m.wastage.toFixed(0)}%. Consider optimizing dimensions.` });
    });

    // Low margin
    const margin = rates.profitMargin || 10;
    if (margin < 8) a.push({ severity: 'error', msg: `Profit margin ${margin}% is dangerously low. Minimum recommended: 8%.` });

    // Missing hardware
    if (drawerCount > 0 && (rates.telescopicDrawerCost || 0) === 0) a.push({ severity: 'error', msg: 'Drawer channel cost is Rs 0. Hardware cost missing.' });

    // Zero rates
    if ((rates.laborCost || 0) === 0) a.push({ severity: 'warn', msg: 'Labor cost is Rs 0. Verify rate input.' });
    if ((rates.ply18mm || 0) === 0) a.push({ severity: 'error', msg: '18mm plywood rate is Rs 0. Material cost will be incorrect.' });

    // All good
    if (a.length === 0) a.push({ severity: 'info', msg: 'All validations passed. Estimate looks good.' });

    return a;
  }, [config, calculations, rates]);

  const errorCount = alerts.filter(a => a.severity === 'error').length;
  const warnCount = alerts.filter(a => a.severity === 'warn').length;

  return (
    <div className="space-y-2" data-testid="error-prevention">
      <div className="flex items-center gap-3">
        {errorCount > 0 ? (
          <div className="flex items-center gap-1 text-red-700 text-xs font-bold"><AlertCircle size={14} />{errorCount} error{errorCount > 1 ? 's' : ''}</div>
        ) : (
          <div className="flex items-center gap-1 text-green-700 text-xs font-bold"><CheckCircle size={14} />No errors</div>
        )}
        {warnCount > 0 && <div className="flex items-center gap-1 text-amber-700 text-xs font-bold"><AlertTriangle size={14} />{warnCount} warning{warnCount > 1 ? 's' : ''}</div>}
      </div>
      {alerts.map((alert, i) => (
        <div key={i} className={`flex items-start gap-2 p-2 rounded-lg border text-xs ${SEVERITY[alert.severity]}`}>
          {alert.severity === 'error' ? <AlertCircle size={14} className="flex-shrink-0 mt-0.5" /> :
           alert.severity === 'warn' ? <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" /> :
           <Info size={14} className="flex-shrink-0 mt-0.5" />}
          <span>{alert.msg}</span>
        </div>
      ))}
    </div>
  );
}
